import { forwardRef, type CSSProperties, type ComponentPropsWithoutRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn, useTheme } from '@vitness/theme';

/**
 * Public props for {@link Switch}. Authored explicitly (rather than re-exporting
 * Radix's prop type) so the component's API surface stays independent of the
 * Radix version and never leaks Radix types to consumers.
 */
export interface SwitchProps
  extends Omit<
    ComponentPropsWithoutRef<'button'>,
    'onChange' | 'defaultChecked' | 'value' | 'type'
  > {
  /** Controlled checked state. */
  checked?: boolean;
  /** Uncontrolled initial checked state. */
  defaultChecked?: boolean;
  /** Fires when the checked state changes. */
  onCheckedChange?: (checked: boolean) => void;
  /** Whether the switch is required in a form. */
  required?: boolean;
  /** Form field name. */
  name?: string;
  /** Form submission value when checked (default `"on"`). */
  value?: string;
}

/**
 * Accent-colored toggle built on Radix Switch. The "on" track uses the active
 * theme accent (via the `--vn-control-accent` CSS variable) so it stays correct
 * across every accent, in both dark and light modes. Keyboard- and
 * screen-reader-accessible out of the box (`role="switch"`, Space/Enter toggle).
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { className, style, ...props },
  ref,
) {
  const { accentSafe } = useTheme();

  return (
    <SwitchPrimitive.Root
      ref={ref}
      style={{ '--vn-control-accent': accentSafe, ...style } as CSSProperties}
      className={cn(
        'peer inline-flex h-[20px] w-[36px] shrink-0 cursor-pointer items-center rounded-full',
        'border border-border-strong bg-surface-inset transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-[var(--vn-control-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-surface-bg',
        'disabled:cursor-not-allowed disabled:opacity-45',
        'data-[state=checked]:border-[var(--vn-control-accent)] data-[state=checked]:bg-[var(--vn-control-accent)]',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block h-[14px] w-[14px] rounded-full bg-surface-bg shadow-vn-card',
          'translate-x-[3px] transition-transform data-[state=checked]:translate-x-[19px]',
        )}
      />
    </SwitchPrimitive.Root>
  );
});
